import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Package, Sparkles, RefreshCw } from 'lucide-react';
import ViewLayout from '../ui/ViewLayout';

// Import all the extracted search components
import {
  // Modals
  WordCloudModal,
  ColorPickerModal,
  TimelineModal,
  WhatAreYouLookingForModal,
  
  // UI Elements
  SpeedDial,
  SearchBar,
  FilterSidebar,
  ActiveFilters,
  ViewToggle,
  SortingControls,
  
  // View Components
  GridView,
  ListView,
  ImagesView,
  WarehousesView,
  SurpriseView
} from '../search';

const SearchView = ({
  searchQuery,
  setSearchQuery,
  selectedFilters,
  setSelectedFilters,
  filteredProps,
  props,
  organizations,
  categories = [],
  transferRequests,
  clearFilters,
  addToCart,
  removeFromCart,
  setSelectedProp,
  handleRequestTransfer,
  userLists = [],
  onAddToList
}) => {
  // View and Modal States
  const [currentView, setCurrentView] = useState('grid');
  const [showWhatModal, setShowWhatModal] = useState(false);
  const [showWordCloud, setShowWordCloud] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);

  // Sorting and Pagination States
  const [sortBy, setSortBy] = useState('distance');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Enhanced Sorting Logic
  const sortedProps = [...filteredProps].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'distance':
        aValue = a.distance || 0;
        bValue = b.distance || 0;
        break;
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'category':
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
        break;
      case 'availability':
        // Priority: available > reserved > checked_out > other
        const statusPriority = { 'available': 0, 'reserved': 1, 'checked_out': 2 };
        aValue = statusPriority[a.status] ?? 3;
        bValue = statusPriority[b.status] ?? 3;
        break;
      case 'owner':
        aValue = a.owner.toLowerCase();
        bValue = b.owner.toLowerCase();
        break;
      case 'recently_added':
        aValue = new Date(a.created_at || 0);
        bValue = new Date(b.created_at || 0);
        break;
      case 'popularity':
        aValue = a.checkout_count || 0;
        bValue = b.checkout_count || 0;
        break;
      default:
        aValue = a.distance || 0;
        bValue = b.distance || 0;
    }
    
    // Handle string vs number comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return sortOrder === 'asc' ? comparison : -comparison;
    } else {
      const comparison = aValue - bValue;
      return sortOrder === 'asc' ? comparison : -comparison;
    }
  });

  // Pagination Logic
  const totalPages = Math.ceil(sortedProps.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProps = sortedProps.slice(startIndex, endIndex);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedFilters, sortBy, sortOrder]);

  // ViewLayout configuration
  const breadcrumbs = [
    { label: 'PropShare Network' },
    { label: 'Search & Browse' }
  ];

  const viewLabels = {
    grid: 'Grid View',
    list: 'List View', 
    images: 'Image Gallery',
    warehouses: 'By Warehouse',
    surprise: 'Surprise Me'
  };

  const title = `Search Props - ${viewLabels[currentView] || 'Search'}`;
  const subtitle = `Browse ${props.length.toLocaleString()} props from ${[...new Set(props.map(p => p.owner))].length} organizations. ${filteredProps.length} match your current filters.`;

  const actions = [
    <button
      key="what"
      onClick={() => setShowWhatModal(true)}
      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2"
    >
      <Sparkles className="h-4 w-4" />
      What are you looking for?
    </button>,
    <button
      key="clear"
      onClick={() => {
        clearFilters();
        setSearchQuery('');
      }}
      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
    >
      <RefreshCw className="h-4 w-4" />
      Clear All
    </button>
  ];

  // Helper function to get category ID by name
  const getCategoryId = (categoryName) => {
    if (!categoryName) return '';
    const category = categories.find(cat => cat.name === categoryName);
    return category ? category.id : '';
  };

  // Modal Handler Functions
  const handleQuickSearch = (category, item) => {
    // Convert the selection to search terms and filters
    const searchTerms = {
      // Furniture
      'Chairs': { category: 'Furniture', search: 'chair' },
      'Tables': { category: 'Furniture', search: 'table' },
      'Sofas': { category: 'Furniture', search: 'sofa' },
      'Desks': { category: 'Furniture', search: 'desk' },
      'Beds': { category: 'Furniture', search: 'bed' },
      'Cabinets': { category: 'Furniture', search: 'cabinet' },
      'Shelving': { category: 'Furniture', search: 'shelf' },
      'Benches': { category: 'Furniture', search: 'bench' },
      
      // Performance
      'Costumes': { category: 'Costumes', search: 'costume' },
      'Props': { search: 'prop' },
      'Instruments': { category: 'Musical Instruments', search: 'instrument' },
      'Scripts': { search: 'script' },
      'Makeup': { search: 'makeup' },
      'Masks': { search: 'mask' },
      'Backdrops': { search: 'backdrop' },
      'Set Pieces': { search: 'set piece' },
      
      // Historical periods
      'Victorian': { search: 'victorian' },
      'Medieval': { search: 'medieval' },
      'Renaissance': { search: 'renaissance' },
      'Modern': { search: 'modern' },
      'Ancient': { search: 'ancient' },
      'Art Deco': { search: 'art deco' },
      '1950s': { search: '1950' },
      'Industrial': { search: 'industrial' },
      
      // Colors
      'Red Items': { search: 'red' },
      'Blue Items': { search: 'blue' },
      'Green Items': { search: 'green' },
      'Yellow Items': { search: 'yellow' },
      'Black Items': { search: 'black' },
      'White Items': { search: 'white' },
      'Gold Items': { search: 'gold' },
      'Silver Items': { search: 'silver' },
      
      // Sizes
      'XS (Handheld)': { search: 'small handheld' },
      'Small': { search: 'small' },
      'Medium': { search: 'medium' },
      'Large': { search: 'large' },
      'XL (Room-sized)': { search: 'large room' },
      'Oversized': { search: 'oversized' },
      
      // Regions
      'European': { search: 'european' },
      'Asian': { search: 'asian' },
      'American': { search: 'american' },
      'African': { search: 'african' },
      'Middle Eastern': { search: 'middle eastern' },
      'Latin American': { search: 'latin american' },
      'Nordic': { search: 'nordic' },
      'Mediterranean': { search: 'mediterranean' }
    };
    
    const searchConfig = searchTerms[item] || { search: item.toLowerCase() };
    
    // Apply the search and filter
    if (searchConfig.category) {
      // Convert category name to ID
      const categoryId = getCategoryId(searchConfig.category);
      if (categoryId) {
        setSelectedFilters({...selectedFilters, category: categoryId});
      }
    }
    if (searchConfig.search) {
      setSearchQuery(searchConfig.search);
    }
    
    // Switch to appropriate view
    setCurrentView('grid');
  };

  const handleWordClick = (wordItem) => {
    // Apply search based on word type
    switch(wordItem.type) {
      case 'category':
        // Special handling for "Uncategorized"
        if (wordItem.text === 'Uncategorized') {
          // Set a special marker to show only uncategorized items
          setSelectedFilters({...selectedFilters, category: 'uncategorized'});
          setSearchQuery(''); // Clear search to show all uncategorized
        } else {
          // Convert category name to ID for regular categories
          const categoryId = getCategoryId(wordItem.text);
          if (categoryId) {
            setSelectedFilters({...selectedFilters, category: categoryId});
          }
        }
        break;
      case 'tag':
      case 'material':
      case 'period':
        setSearchQuery(wordItem.text);
        break;
    }
    setCurrentView('grid');
  };

  const handleColorSelect = (color) => {
    setSearchQuery(color.searchTerms[0]);
    setCurrentView('grid');
  };

  const handlePeriodSelect = (period) => {
    setSearchQuery(period.keywords[0]);
    setCurrentView('grid');
  };

  // Speed Dial Action Handlers
  const handleSurpriseMe = () => {
    setCurrentView('surprise');
  };

  const handleClearFilters = () => {
    clearFilters();
    setSearchQuery('');
  };

  const handleShowAvailableOnly = () => {
    setSelectedFilters({...selectedFilters, availability: 'available'});
    setSortBy('distance');
    setSortOrder('asc');
  };

  // View Renderer
  const renderPropsView = () => {
    const commonProps = {
      paginatedProps,
      totalPages,
      currentPage,
      onPageChange: setCurrentPage,
      addToCart,
      removeFromCart,
      setSelectedProp,
      handleRequestTransfer,
      userLists,
      onAddToList
    };

    switch (currentView) {
      case 'grid':
        return <GridView {...commonProps} />;
      
      case 'list':
        return <ListView {...commonProps} />;
      
      case 'images':
        return <ImagesView {...commonProps} />;
      
      case 'warehouses':
        return (
          <WarehousesView 
            {...commonProps}
            organizations={organizations}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            setCurrentView={setCurrentView}
          />
        );
      
      case 'surprise':
        return (
          <SurpriseView 
            {...commonProps}
            sortedProps={sortedProps}
            setCurrentView={setCurrentView}
          />
        );
      
      default:
        return (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-4">🚧</div>
            <p className="text-lg font-medium mb-2">View Under Development</p>
            <p className="text-sm">This view is coming soon!</p>
          </div>
        );
    }
  };

  return (
    <ViewLayout
      title={title}
      subtitle={subtitle}
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filter Sidebar */}
        <div className="lg:col-span-1">
          <FilterSidebar
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            clearFilters={clearFilters}
          />
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          {/* Search Bar with Discovery Tools */}
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            props={props}
            onShowWhatModal={() => setShowWhatModal(true)}
            onShowWordCloud={() => setShowWordCloud(true)}
            onShowColorPicker={() => setShowColorPicker(true)}
            onShowTimeline={() => setShowTimeline(true)}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />

          {/* View Toggle */}
          <ViewToggle
            currentView={currentView}
            setCurrentView={setCurrentView}
          />

          {/* Active Filters */}
          <ActiveFilters
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            clearFilters={clearFilters}
            categories={categories}
          />

          {/* Sorting Controls & Results Summary */}
          <SortingControls
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            filteredProps={filteredProps}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage}
          />

          {/* Props View */}
          <div className="space-y-4">
            {renderPropsView()}
          </div>
        </div>
      </div>

      {/* Modals */}
      <WhatAreYouLookingForModal
        isOpen={showWhatModal}
        onClose={() => setShowWhatModal(false)}
        onQuickSearch={handleQuickSearch}
      />

      <WordCloudModal
        isOpen={showWordCloud}
        onClose={() => setShowWordCloud(false)}
        props={props}
        onWordClick={handleWordClick}
      />

      <ColorPickerModal
        isOpen={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        props={props}
        onColorSelect={handleColorSelect}
      />

      <TimelineModal
        isOpen={showTimeline}
        onClose={() => setShowTimeline(false)}
        props={props}
        onPeriodSelect={handlePeriodSelect}
      />

      {/* Speed Dial - Hidden for now */}
      {false && (
        <SpeedDial
          onSurpriseMe={handleSurpriseMe}
          onClearFilters={handleClearFilters}
          onShowAvailableOnly={handleShowAvailableOnly}
          onShowWordCloud={() => setShowWordCloud(true)}
          onShowColorPicker={() => setShowColorPicker(true)}
          onShowTimeline={() => setShowTimeline(true)}
        />
      )}
    </ViewLayout>
  );
};

export default SearchView;